import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import api from "../utils/api";
import ClientForm from "../components/ClientForm";

const Clients = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/clients");
      setClients(response.data.clients || []);
      setError("");
    } catch (err) {
      console.error("Failed to fetch clients:", err);
      setError(err.response?.data?.error || "Failed to load clients");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleDelete = async (row) => {
    if (row.name === "Nikao - Internal") {
      setError("Cannot delete the seeded Nikao - Internal client");
      return;
    }
    if (!window.confirm(`Delete client "${row.name}"?`)) return;
    try {
      await api.delete(`/api/clients/${row.id}`);
      fetchClients();
    } catch (err) {
      console.error("Failed to delete client:", err);
      setError(err.response?.data?.error || "Failed to delete client");
    }
  };

  const handleEdit = (row) => {
    setEditingClient(row);
    setOpenForm(true);
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setEditingClient(null);
  };

  const handleFormSubmit = async (payload) => {
    if (editingClient) {
      await api.put(`/api/clients/${editingClient.id}`, payload);
    } else {
      await api.post("/api/clients", payload);
    }
    handleFormClose();
    fetchClients();
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 1.4, minWidth: 180 },
    { field: "clientType", headerName: "Type", width: 110, minWidth: 100 },
    { field: "status", headerName: "Status", width: 110, minWidth: 100 },
    {
      field: "accountOwner",
      headerName: "Account owner",
      flex: 1,
      minWidth: 140,
    },
    { field: "industry", headerName: "Industry", flex: 1, minWidth: 120 },
    {
      field: "primaryContactEmail",
      headerName: "Contact email",
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEdit(params.row)}
            aria-label="Edit"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row)}
            aria-label="Delete"
            disabled={params.row.name === "Nikao - Internal"}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box width="100%">
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingClient(null);
            setOpenForm(true);
          }}
        >
          Add Client
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Card sx={{ width: "100%" }}>
        <CardContent sx={{ width: "100%", "&:last-child": { pb: 2 } }}>
          <Box height="calc(100vh - 220px)" minHeight={480} width="100%">
            <DataGrid
              rows={clients}
              columns={columns}
              loading={loading}
              getRowId={(row) => row.id}
              disableSelectionOnClick
              disableColumnMenu
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              sx={{
                width: "100%",
                border: 0,
                "& .MuiDataGrid-main": { width: "100%" },
                "& .MuiDataGrid-virtualScroller": { width: "100%" },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={openForm}
        onClose={handleFormClose}
        maxWidth={fullScreen ? false : "md"}
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle>
          {editingClient ? "Edit Client" : "Add New Client"}
        </DialogTitle>
        <DialogContent>
          <ClientForm
            client={editingClient}
            onSubmit={handleFormSubmit}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Clients;
